<template>
	<van-popup
		v-model:show="showModal"
		:close-on-click-overlay="!payLoading"
		position="bottom"
		safe-area-inset-top
		safe-area-inset-bottom
		class="m-pay-popup"
		close-on-popstate
		transition-appear
	>
		<div class="close">
			<img src="./images/close.png" alt="" @click="showModal = false" />
		</div>
		<div class="coupon">
			<span class="label">{{ t("user.pc.coupon.title") }}</span>
			<span class="coupon-text" v-if="coupon">{{ coupon }} </span>
			<span class="no-coupon" v-else>{{ t("user.pc.wallet.noCoupon") }}</span>
		</div>
		<h3 class="pay-title">{{ t("user.pc.wallet.selectPayArea") }}</h3>
		<!-- 地区列表 -->
		<div class="area">
			<ul class="tab" :class="{ 'no-more': !showMore }">
				<li
					:class="['tab-item', { active: item.name === currentArea }]"
					v-for="(item, index) in areaPayment"
					:key="index"
					@click="currentArea = item.name"
				>
					{{ displayPayAreaName(item.name, t) }}
				</li>
			</ul>
			<div v-if="!showMore" class="more" @click="showMore = true">
				{{ t("user.pc.common.loadMore") }}
				<img src="./images/icon_more.png" alt="" />
			</div>
		</div>

		<h3 class="pay-title">{{ t("user.pc.wallet.selectPayment") }}</h3>
		<!-- 支付列表 -->
		<ul class="pay-list">
			<li v-for="(item, index) in payList" :key="index" @click="curPayId = index">
				<div :class="['pay-item', { active: index === curPayId }]">
					<van-image class="icon" :src="resolvePaymentIcon(item)" fit="cover" alt=""></van-image>
					<p>
						{{ displayPayMethodName(item, t) }}
						<span class="coin">{{ isCoinMethod(item) ? `(${coin})` : "" }}</span>
					</p>
				</div>
			</li>
		</ul>
		<!-- 支付按鈕 -->
		<div class="btn-pay">
			<div class="btn-pay-top">
				<div class="price-box">
					<div class="price">
						<span class="unit">us$</span>
						<span class="price-text">{{ finalPrice }}</span>
						<span v-if="coupon" class="tip">{{ t("user.pc.wallet.discountedPrice") }}</span>
					</div>
					<p class="orgin-price" v-if="coupon">
						{{ t("user.pc.wallet.originalPriceCoin", { amount: orderInfo.amount }) }}
					</p>
				</div>
				<div class="pay-btn-box">
					<van-button
						:class="[
							'btn',
							{ gray: !isEnoughCoin && isCoinPay },
							{ 'btn--en-insufficient': isEnglishLocale && !isEnoughCoin && isCoinPay },
						]"
						:loading="payLoading"
						:loading-text="t('user.pc.wallet.paymentProcessing')"
						@click="handlePay"
					>
						{{ btnText }}
					</van-button>
				</div>
			</div>
			<MPayAgree class="m-agree-box" v-model="isAgree" />
		</div>
	</van-popup>
</template>

<script setup>
import { ref, watch, watchEffect, computed } from "vue";
import showMessage from "~/utils/showMessage";
import { getUserCoin, coinPay, checkPay } from "@/api/user";
import usePixel from "~/hooks/usePixel";
import { useI18n } from "~/composables/useI18n";
import { resolvePaymentIcon } from "@variants/seer/user/pc/components/MyWallet/payIcons";
import { displayPayAreaName, displayPayMethodName } from "~/components/pay/payment-i18n";
import MPayAgree from "~/components/pay/m-pay-agree.vue";
const { reportGooglePixelEvent, reportPurchase } = usePixel();
const { t, locale } = useI18n();
const isEnglishLocale = computed(() => locale.value === "en");

const props = defineProps({
	finalPrice: {
		type: [String, Number],
		default: 0,
	},
	orderInfo: {
		type: Object,
		default: () => ({}),
	},
	close: {
		type: Function,
		default: () => {},
	},
	areaPayment: {
		type: Array,
		default: () => [],
	},
	onSuccess: {
		type: Function,
		default: () => {},
	},
});

const showModal = ref(true); //支付彈窗
watchEffect(() => {
	if (!showModal.value) {
		setTimeout(() => {
			props.close();
		}, 300);
	}
});

const closePayModal = () => {
	if (payLoading.value) return;
	showModal.value = false;
};

const displayAmount = computed(() => {
	const amount = Number(props.finalPrice);
	if (Number.isNaN(amount)) return "0.00";
	return amount.toFixed(2);
});
const showMore = ref(false);

const currentArea = ref();
watch(
	() => props.areaPayment,
	() => {
		currentArea.value = props.areaPayment[0]?.name;
	},
	{
		immediate: true,
	}
);
const payList = computed(
	() => props.areaPayment.find(v => v.name === currentArea.value)?.list || []
);
const curPayId = ref(0); // 當前選擇的支付方式索引
watch(
	() => currentArea.value,
	() => {
		curPayId.value = 0;
	},
	{
		immediate: true,
	}
);
watch(payList, list => {
	if (curPayId.value >= list.length) {
		curPayId.value = 0;
	}
});
const selectedPayMethod = computed(() => payList.value[curPayId.value] || null);
const isCoinMethod = item => /coin|點數|点数/i.test(item?.name || "") || item?.type === "coin";
const isCoinPay = computed(() => isCoinMethod(selectedPayMethod.value));

const coupon = computed(() => props.orderInfo.order_coupon?.discount_tag);

const isAgree = ref(true);

// 支付處理
const handlePay = async () => {
	if (!isAgree.value) {
		showMessage(t("user.pc.wallet.agreementRequired"), "error");
		return;
	}
	const loading = showLoadingToast({
		forbidClick: true, //禁用背景点击
		duration: 0, //值为 0 时，toast 不会消失
	});
	reportGooglePixelEvent("gw_pay_event");

	const { third_order_id } = props.orderInfo;
	const urlList = props.orderInfo.area_payment.find(v => v.name === currentArea.value).list;
	const payUrl = urlList.find(v => v.name === selectedPayMethod.value?.name)?.url;
	// 點數支付
	if (isCoinPay.value) {
		loading.close();
		return toCoinPay(third_order_id);
	}
	if (!payUrl) {
		loading.close();
		return;
	}

	// 第三方支付
	if (import.meta.client) {
		localStorage.setItem("order_id", third_order_id);

		// 监听页面可见性变化，当页面隐藏时关闭loading
		const visibilityHandler = () => {
			if (document.hidden) {
				loading.close();
				document.removeEventListener("visibilitychange", visibilityHandler);
			}
		};
		document.addEventListener("visibilitychange", visibilityHandler);

		// 设置超时，如果5秒后还没跳转则关闭loading
		const timeoutId = setTimeout(() => {
			loading.close();
			document.removeEventListener("visibilitychange", visibilityHandler);
		}, 5000);

		try {
			window.location.href = payUrl;
		} catch (error) {
			// 如果跳转失败，立即关闭loading
			loading.close();
			document.removeEventListener("visibilitychange", visibilityHandler);
			clearTimeout(timeoutId);
			showMessage(t("user.pc.wallet.jumpPayFailed"), "error");
			return;
		}

		setTimeout(() => {
			checkPayData(third_order_id, 40, 3000);
		}, 300);
	}
};

// 點數支付
const coin = ref(0);
getUserCoin().then(res => {
	coin.value = res.data.coin_num;
});
const isEnoughCoin = computed(() => {
	if (coin.value === "-") return false;
	return Number(coin.value) >= Number(props.finalPrice);
});
const payLoading = ref(false);
// const router = useRouter(); // Use Nuxt's navigateTo instead
const toCoinPay = async orderId => {
	if (payLoading.value) return;
	if (!isEnoughCoin.value) {
		await navigateTo("/seer/user?type=wallet");
		showModal.value = false;
		return;
	}
	payLoading.value = true;
	await coinPay({
		order_id: orderId,
	}).catch(() => {
		payLoading.value = false;
	});
	checkPayData(orderId);
};

// 查詢是否支付完成

let currentCheckController = null; // 当前查单的控制器

const checkPayData = async (orderId, retry = 5, ms = 3000) => {
	// 如果有正在进行的查单，先取消它
	if (currentCheckController) {
		currentCheckController.abort();
	}

	// 创建新的控制器
	currentCheckController = new AbortController();
	const signal = currentCheckController.signal;

	try {
		const isPay = await checkOrderStatus(orderId, retry, ms, signal);

		// 检查是否被取消
		if (signal.aborted) return;

		if (isPay) {
			localStorage.removeItem("order_id");
			showMessage(t("user.pc.wallet.paymentSuccess"), "success");
			props.onSuccess(successData.value);

			// 第三方像素支付事件回传
			reportPurchase(orderId, props.finalPrice);

			showModal.value = false;
		} else {
			showMessage(t("user.pc.wallet.paymentNotFound"), "error");
		}
	} catch (error) {
		// 如果是取消操作，不显示错误
		if (error.name !== "AbortError") {
			showMessage(t("user.pc.wallet.checkPayFailed"), "error");
		}
	} finally {
		payLoading.value = false;
		currentCheckController = null;
	}
};

// 查單
const successData = ref({});
async function checkOrderStatus(orderId, retry = 10, ms = 3000, signal = null) {
	if (retry < 0) return false;

	if (typeof retry !== "number") {
		retry = 10;
	}

	// 检查是否被取消
	if (signal && signal.aborted) {
		throw new DOMException("Operation was aborted", "AbortError");
	}

	const { data } = await checkPay({
		order_ids: orderId,
	});

	if (!Array.isArray(data.list) || !data.list.length || data.list[0].status === 0) {
		return new Promise((resolve, reject) => {
			const timeoutId = setTimeout(() => {
				// 再次检查是否被取消
				if (signal && signal.aborted) {
					reject(new DOMException("Operation was aborted", "AbortError"));
					return;
				}
				resolve(checkOrderStatus(orderId, retry - 1, ms, signal));
			}, ms);

			// 监听取消信号
			if (signal) {
				signal.addEventListener("abort", () => {
					clearTimeout(timeoutId);
					reject(new DOMException("Operation was aborted", "AbortError"));
				});
			}
		});
	} else {
		successData.value = data.list[0];
		return data.list[0].status;
	}
}

// 支付按鈕文案
const btnText = computed(() => {
	let text = t("user.pc.wallet.payNow");
	if (isCoinPay.value && !isEnoughCoin.value) {
		text = t("user.pc.wallet.balanceInsufficient");
	}
	return payLoading.value ? t("user.pc.wallet.paymentProcessing") : text;
});
</script>

<style lang="scss" scoped>
.m-pay-popup {
	display: flex;
	flex-direction: column;
	width: 750px;
	padding-bottom: 220px;
	box-sizing: border-box;
	background-color: #ffffff;
	border-radius: 22px 22px 0px 0px;

	.close {
		display: flex;
		padding-left: 28px;
		width: 750px;
		height: 102px;
		box-sizing: border-box;

		img {
			margin: auto 0;
			width: 44px;
			height: 44px;
		}
	}

	.coupon {
		margin: 36px auto 0;
		padding: 30px 20px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 686px;
		height: 106px;
		background: #ffffff;
		border-radius: 14px 14px 14px 14px;
		border: 2px solid rgba(0, 0, 0, 0.05);

		.label {
			font-weight: 500;
			font-size: 33px;
			color: #000000;
			line-height: 38px;
		}

		.coupon-text {
			padding: 2px 10px;
			font-weight: 500;
			font-size: 24px;
			color: #c62000;
			line-height: 36px;
			background: #ffe4e2;
			border-radius: 4px 4px 4px 4px;
		}

		.no-coupon {
			font-size: 27px;
			color: rgba(0, 0, 0, 0.7);
			line-height: 32px;
		}
	}

	.pay-title {
		margin: 44px 32px 0;
		padding: 0;
		font-weight: 700;
		font-size: 32px;
		color: #333333;
		line-height: 42px;
	}

	.area {
		margin-bottom: 36px;
		display: flex;
		padding: 0 32px;

		.tab {
			flex: 1;
			display: flex;
			flex-wrap: wrap;
			font-size: 26px;
			overflow: hidden;

			.tab-item {
				width: fit-content;
				padding: 0 24px;
				height: 88px;
				background: #ffffff;
				border-radius: 16px;
				margin-right: 12px;
				display: flex;
				justify-content: center;
				align-items: center;
				color: #666666;
				margin-bottom: 12px;
			}

			.active {
				background: #111111;
				border: 1px solid #111111;
				color: #ffffff;
				font-weight: 700;
			}
		}

		.more {
			display: flex;
			align-items: center;
			justify-content: center;
			white-space: nowrap;
			width: 168px;
			height: 88px;
			background: #ffffff;
			border-radius: 16px;
			font-size: 28px;
			color: rgba(0, 0, 0, 0.6);
			line-height: 88px;

			img {
				width: 36px;
				height: 36px;
				margin-left: 12px;
			}
		}
	}

	.no-more {
		height: 88px;
	}

	.pay-list {
		margin-top: 30px;
		padding: 0 32px;
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;

		li {
			width: 48%;
			height: 90px;
			margin-bottom: 23px;
			box-sizing: border-box;
			cursor: pointer;

			.pay-item {
				padding: 0 15px 0 20px;
				width: 100%;
				height: 100%;
				display: flex;
				align-items: center;
				position: relative;
				overflow: hidden;
				box-shadow: none;
				border-radius: 8px;
				background-color: #ffffff;
				border: 0.5px solid #dfdfdf;

				.icon {
					width: 58px;
					height: 58px;
					margin-right: 20px;
					border-radius: 50%;
				}

				p {
					font-size: 23px;
					color: #000000;
					line-height: 32px;
				}

				.coin {
					white-space: nowrap;
				}
			}

			.active {
				background: #111111;
				border: 1px solid #111111;
				position: relative;

				p {
					color: #ffffff;
				}

				&::after {
					content: none;
				}
			}
		}
	}
	.btn-pay {
		position: fixed;
		bottom: 0;
		left: 0;
		z-index: 2;
		display: block;
		padding: 24px 32px 38px;
		width: 750px;
		height: 192px;
		box-sizing: border-box;
		background: #ffffff;
		box-shadow: 0px -6px 14px 0px rgba(0, 0, 0, 0.04);

		.btn-pay-top {
			display: flex;
			align-items: center;
			justify-content: space-between;
		}

		.m-agree-box {
			justify-content: flex-end;
		}

		.price-box {
			.orgin-price {
				font-size: 20px;
				color: #999999;
				line-height: 28px;
			}
		}
		.price {
			position: relative;

			.unit {
				font-weight: 600;
				font-size: 33px;
				color: #c57328;
				line-height: 38px;
			}

			.price-text {
				padding-right: 10px;
				font-weight: 600;
				font-size: 51px;
				color: #c57328;
				line-height: 59px;
			}

			.tip {
				margin-left: 6px;
				padding: 0 6px;
				font-weight: 600;
				font-size: 20px;
				color: #ffffff;
				line-height: 36px;
				background: linear-gradient(90deg, #fb483d 0%, #d22727 100%);
				border-radius: 16px 16px 16px 2px;
				position: absolute;
				top: 0;
				right: 0;
				transform: translate(100%, 86%);
			}
		}

		.pay-btn-box {
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			white-space: nowrap;
		}

		.btn {
			height: 102px;
			font-weight: 600;
			font-size: 34px;
			color: #ffffff;
			line-height: 102px;
			text-align: center;
			background: #111111;
			border-radius: 14px;

			&.gray {
				filter: grayscale(100%);
			}

			&.btn--en-insufficient {
				line-height: 40px;

				:deep(.van-button__text) {
					line-height: 40px;
					white-space: normal;
				}
			}
		}
	}

	.pay-confirm-popup {
		background-color: transparent;
		overflow: visible;

		.pay-confirm-container {
			position: relative;
			width: 400px;
			height: 225px;
			background-color: #2b1a11;
			border-radius: 20px;
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;

			.pay-confirm-close-icon {
				position: absolute;
				top: -20px;
				right: 0;
				transform: translateY(-100%);
				font-size: 60px;
				color: #cfb595;
			}

			.pay-confirm-title {
				font-size: 32px;
				color: #cfb595;
				white-space: nowrap;
			}

			.pay-confirm-btn {
				margin-top: 30px;
				height: 80px;
				font-size: 36px;
				font-weight: 600;
				color: #fff5dd;
				line-height: 52px;
				background: #be4432;
				border: 1px solid #ffe7aa;
				border-radius: 20px;
			}
		}
	}
}
</style>
